import { Fan } from "@musicaudienceexchange/ap-interface/dist/esm/firestore";
import { Button } from "melodies-source/Button";
import { useArtistContext } from "Components";
import { DateTime } from "luxon";
import { useState } from "react";
import { formatPhoneNumber } from "Utils";
import { downloadCSV } from "Utils/csv";
import { useFanTableContext } from "./";
import { useTypesenseProxyContext } from "contexts/TypesenseProxyContext";
import { SvgDownload } from "melodies-source/Svgs/Download";
import { getAuth, getIdToken } from "firebase/auth";
import { downloadFromBlob } from "Utils/file";
import { SearchParams } from "contexts/TypesenseProxyContext";

interface ExportFansRequestPayload {
  artistGroupId: string;
  searchParameters: SearchParams;
}

export const Download = () => {
  const [downloadLoading, setDownloadLoading] = useState(false);

  const { proxySearch } = useTypesenseProxyContext();
  const { searchParameters, found } = useFanTableContext();
  const { name: artistName, logAction, artistGroupDoc, secureEnabled } = useArtistContext();

  const generateSecureFansReport = async (): Promise<void> => {
    const url = process.env.REACT_APP_FANS_REPORT_SERVER;
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error("No authenticated user");
    }
    const token = await getIdToken(auth.currentUser);
    
    const requestBody: ExportFansRequestPayload = {
      artistGroupId: artistGroupDoc.id,
      searchParameters,
    };

    const req = new Request(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `bearer ${token}`
      },
      body: JSON.stringify(requestBody)
    });

    const response = await fetch(req);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Convert the response to a blob and create a blob URL for download
    const csvBlob = await response.blob();
    const filename = `${artistName}-${DateTime.now().toLocaleString(DateTime.DATE_SHORT)}-fans.csv`;
    downloadFromBlob(csvBlob, filename);
  };

  const generateTypesenseFansReport = async (): Promise<void> => {
    let pagecount = found / 250;
    let requests = [];
    for (let page = 0; page < pagecount; page++) {
      let req = await new Promise<any[]>(async (resolve, reject) => {
        proxySearch("contacts", {
          ...searchParameters,
          page: page + 1,
          per_page: 250,
        }).then((res) => {
          let docs = res.hits.map(({ document }) => document);
          resolve(docs);
        });
      });
      requests.push(req);
    }

    let csvData = [];

    try {
      for (const req of requests) {
        const results = await req;

        //  const results = await Promise.all(requests);

        csvData = csvData.concat(
          results
            //.flat()
            .map((doc: Omit<Fan, "createdAt"> & { createdAt: number }) => {
              const {
                name,
                location,
                age,
                gender,
                createdAt,
                phone,
                email,
                city,
                state,
                zip,
                country,
              } = doc;

              return [
                name || "",
                location || "",
                age || "",
                gender || "",
                (
                  DateTime.fromMillis(createdAt).toLocaleString(
                    DateTime.DATETIME_SHORT,
                  ) || ""
                ).replace(",", ""),
                formatPhoneNumber(phone) || "",
                email || "",
                city || "",
                state || "",
                zip || "",
                (country || "").toUpperCase(),
              ];
            }),
        );
      }

      await logAction("portal_download_fans", { searchParameters, found });

      downloadCSV(
        FANS_HEADERS,
        csvData as string[][],
        `${artistName}-${DateTime.now().toLocaleString(
          DateTime.DATE_SHORT,
        )}-fans.csv`,
      );
    } catch (error) {
      console.error(error);
    }
  }

  const onClickDownload = async () => {
    setDownloadLoading(true);
    if (secureEnabled) {
      await generateSecureFansReport();
    } else {
      await generateTypesenseFansReport();
    }

    setDownloadLoading(false);
  };

  return (
    <Button
      onClick={onClickDownload}
      disabled={downloadLoading}
      style={{ width: "180px", minWidth: "180px" }}
    >
      {downloadLoading ? (
        "Loading"
      ) : (
        <>
          <SvgDownload style={{ marginRight: 10, marginLeft: "-10px" }} />{" "}
          Download
        </>
      )}
    </Button>
  );
};

const FANS_HEADERS = [
  "Name",
  "Location",
  "Age",
  "Gender",
  "Added",
  "Phone",
  "Email",
  "City",
  "State",
  "Zip",
  "Country",
];
